import '@bv/assets/styles/tailwind.css';

import Clarity from '@microsoft/clarity';
import moment from 'moment';
import { h, defineComponent } from 'vue';
import { configure } from 'vue-gtag';

import type { Token } from '@bv/api';
import { components, form as formComponents, voip as voipComponents } from '@bv/components';
import type { CallWidgetOptions } from '@bv/components/voip/call/widget/BvCallWidget.vue';
import { useCookies, useHostname, useWatchRouterPermissions } from '@bv/composables';
import { useToast } from '@bv/composables';
import { useBrandStore, useSipStore } from '@bv/stores';
import { useAuthStore } from '@bv/stores/auth';
import { useSettingsStore } from '@bv/stores/settings';
import { useUserStore } from '@bv/stores/user';
import { LanguageCode } from '@bv/types/plugins/i18n';
import { getQueryParams } from '@bv/utils';
import { trans } from '@bv/utils';
import { getQueryString } from '@bv/utils/route/query-string';
import callWidget from '@bv/utils/voip/sip/widget';

import { registerReloadOnBfCacheRestore } from './bf-cache-restore-reload';
import { directives } from './directives';
import { createI18n, switchLanguage, type I18nOptions as I18nOptionsBase } from './i18n';
import { default as localPinia } from './pinia';
import { createRouter, createEvaluateRouteAccess, setRouter, getRouter, type Sidebar } from './router';
import { default as toastify } from './toastify';
import { default as vueQuery } from './vue-query';
import { default as vuetify } from './vuetify';

import type { Pinia } from 'pinia';
import type { App, Plugin } from 'vue';
import type { Router, RouteRecordRaw } from 'vue-router';

export { components, formComponents, voipComponents, directives, localPinia as pinia, toastify, vuetify, getRouter };

export interface SipOptions {
  connection?: 'connect' | 'register' | false;
  port?: number;
  widget?: CallWidgetOptions;
}

type I18nMessages = I18nOptionsBase['messages'] | I18nOptionsBase['messages'][];

export type I18nOptions = Omit<I18nOptionsBase, 'messages'> & {
  messages?: I18nMessages;
  pluralizationRules?: Record<string, (choice: number, choicesLength: number) => number>;
};

/**
 * Configuration options for the `createBv` plugin.
 * Each property can be set to `false` to disable the corresponding feature.
 */
type Options = {
  /**
   * Options to control the installation of components.
   * If `false`, no components are installed.
   */
  components?: false;

  /**
   * If `false`, disables the installation of global directives.
   */
  directives?: false;

  /**
   * The mode for the SIP (Session Initiation Protocol) connection.
   * 'connect': Connects to the SIP server.
   * 'register': Registers with the SIP server.
   * `false`: Disables sip.
   */
  sip?: SipOptions | false;

  /**
   * Options for the i18n (internationalization) plugin.
   * If `false`, disables i18n.
   * You can pass an object with `I18nOptions` to customize it.
   * You can also pass a single messages object or an array of them
   * to be merged with the default messages.
   */
  i18n?: I18nOptions | false;

  /**
   * An optional Pinia instance.
   * If provided, the plugin will use this instance, allowing for shared state with the main application.
   * If not provided, the plugin will use its own internal Pinia instance.
   */
  pinia?: Pinia;

  /**
   * @deprecated Use `bootstrapUser: false` to skip user bootstrap behavior.
   * Legacy option for disabling identity-related request flow.
   * Options for disabled identity request.
   * If `false`, disables the request.
   * for projects use package but no need for identity request
   */
  brand?: false;

  /**
   * When `true` (default), reads auth cookies on install and may call `getUserDetails` when a token exists.
   * Set to `false` for contexts that supply mocked user state (e.g. Storybook) so the API does not overwrite it.
   */
  bootstrapUser?: boolean;

  /**
   * When `true` (default), reload the document if the browser restores this tab from the
   * back-forward cache (`pageshow` with `persisted`), so the SPA bootstraps again.
   * Set to `false` for Storybook or other hosts where a forced reload is undesirable.
   */
  bfCacheReloadOnRestore?: boolean;

  /**
   * When `true`, automatically injects the auth routes into the mainRoute configuration.
   */
  injectAuthRoutes?: boolean;
} & RouterOptions;

type RouterOptions = OptionsWithRouter | InjectRoutesOptions;

interface OptionsWithRouter {
  /**
   * An existing Vue Router instance.
   * Pass this if your application already has a router set up and you want the plugin
   * to utilize it (e.g., for analytics or navigation guards).
   */
  router?: Router;
}

interface InjectRoutesOptions {
  /**
   * The primary route configuration for the application.
   * If provided, the plugin will automatically create and configure a router instance
   * using this as the root/main entry point.
   */
  mainRoute?: RouteRecordRaw;
  /**
   * @deprecated
   * this option is useless and will be removed.
   */
  layout?: 'old' | 'new';

  /**
   * Configuration for the application's sidebar navigation and actions.
   * Used only when `mainRoute` is provided to generate the application shell.
   */
  sidebar?: Sidebar;
}

/**
 * A Vue plugin that provides a streamlined way to initialize and configure a variety of features
 * in your Vue application.
 *
 * @param {Options} [options] - Configuration options to customize which plugins and features are installed.
 * @returns {Plugin} A Vue plugin object.
 */
export const createBv = (options: Options = {}): Plugin => {
  return {
    install: (app: App) => {
      // Determine which Pinia instance to use: the one passed in options, or the local fallback.
      const piniaInstance = options.pinia || localPinia;

      // Install Pinia with the determined instance.
      app.use(piniaInstance);

      // Install Vuetify plugin.
      app.use(vuetify);

      // Install Toastify plugin.
      app.use(toastify);

      // Install Vue Query plugin.
      app.use(vueQuery);

      // Install components plugins unless explicitly disabled.
      if (options.components !== false) {
        app.use(components);
      }

      // Install Directives plugin unless explicitly disabled.
      if (options.directives !== false) {
        app.use(directives);
      }

      let router: Router | undefined;

      // If a 'mainRoute' is defined in the options, the plugin takes responsibility for
      // creating and configuring the Vue Router. It sets up the application shell,
      // including the sidebar structure, and installs the router instance to the app.
      if ('mainRoute' in options && options.mainRoute) {
        router = createRouter({
          mainRoute: options.mainRoute,
          sidebar: options.sidebar,
          layout: options.layout,
          injectAuthRoutes: options.injectAuthRoutes,
        });
      } else if ('router' in options && options.router) {
        router = options.router;
        setRouter(router);
        // Set up reactive permission watching for passed-in routers
        const evaluateRouteAccess = createEvaluateRouteAccess(router);
        useWatchRouterPermissions(router, evaluateRouteAccess);
      }

      if (router) {
        router.addRoute({
          path: '/health-check',
          name: 'health-check',
          meta: {
            public: true,
          },
          component: defineComponent({
            render: () => h('div', 'ok'),
          }),
        });

        app.use(router);
      }

      // Initialize user data, passing the active Pinia instance.
      initializeUserData(
        {
          sip: options.sip === false ? undefined : options.sip || undefined,
          skipUserFetch: options.bootstrapUser === false,
        },
        piniaInstance,
        router,
        options.brand,
      );

      // Install i18n plugin unless explicitly disabled.
      // Pass any i18n-specific options provided.
      if (options.i18n !== false) {
        const i18n = createI18n(options.i18n || {});
        app.use(i18n);
      } else {
        const i18n = createI18n({ setLocale: false });
        app.use(i18n);

        switchLanguage(LanguageCode.EN, false, false);

        useSettingsStore(piniaInstance).setI18nEnabled(false);
      }

      if (options.bfCacheReloadOnRestore !== false) {
        registerReloadOnBfCacheRestore();
      }

      // Wait for the router to resolve the initial navigation before reading query params.
      if (router) {
        router
          .isReady()
          .then(() => {
            const currentRoute = router.currentRoute.value;
            const { showOtpWarning, ...rest } = currentRoute.query;
            if (getQueryString(showOtpWarning) === '1') {
              const toast = useToast();
              toast.warning(trans('auth.otp.warning_trans'), {
                position: 'top-center',
                autoClose: false,
                dedupe: true,
              });
              // Remove the param while preserving other query values and the hash
              router.replace({ path: currentRoute.path, hash: currentRoute.hash, query: rest });
            }
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('[bv] initial navigation failed', err);
          });
      }
    },
  };
};
/**
 * Checks for an authentication token in cookies and fetches user details from the store
 * if a token is found and user data has not been loaded yet. This function ensures
 * that the user's authentication state is automatically managed upon plugin initialization.
 *
 * @param {object} [options] - Optional configuration object.
 * @param {'connect' | 'register'} [options.sip] - SIP options passed to the SIP store.
 * @param {boolean} [options.skipUserFetch] - When true, skips cookie token handling and `getUserDetails` (used when `createBv({ bootstrapUser: false })`).
 * @param {Pinia} piniaInstance - The active Pinia instance.
 * @returns {Promise<void>} A Promise that resolves when the user data has been fetched, or rejects if an error occurs.
 */
const initializeUserData = async (
  { sip, skipUserFetch }: { sip?: SipOptions; skipUserFetch?: boolean } = {},
  piniaInstance: Pinia,
  router?: Router,
  brand?: boolean,
): Promise<void> => {
  const { config } = useCookies();
  const { isSuperAdmin } = useHostname();
  // Pass the pinia instance to the stores hook to correctly instantiate it.
  const userStore = useUserStore(piniaInstance);
  const brandStore = useBrandStore(piniaInstance);
  const sipStore = useSipStore(piniaInstance);
  const authStore = useAuthStore(piniaInstance);

  sipStore.setOptions(sip);

  // Apply the default configuration when the composable is initialized.
  config();

  if (skipUserFetch || brand === false) {
    return;
  }

  // Initiate white label identity
  brandStore.initBrand();

  if (!isSuperAdmin) {
    const impersonateToken = getQueryParams().impersonate_token;
    if (impersonateToken) {
      const { access_token, expires_at } = JSON.parse(impersonateToken) as Token;
      if (expires_at != null) {
        const expires = Math.trunc(Math.abs(moment().diff(expires_at)) / 1000);
        authStore.setImpersonateTokens(access_token, expires_at, expires);
      }
    }
  }

  authStore.syncTokensFromCookies();

  // Check if the auth token exists in cookies / store
  const token: string | null = authStore.impersonateToken || authStore.token || authStore.refreshToken;

  if (token) {
    if (sip) {
      callWidget.initWidget(sip.widget);
    }

    await userStore.getUserDetails({ forceUpdate: true });

    if (!userStore.userDetails || !router) {
      return;
    }

    if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
      configure({
        tagId: import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
        config: {
          user_id: userStore.userDetails.id,
        },
        pageTracker: {
          router,
        },
      });
    }

    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    if (clarityProjectId) {
      Clarity.init(clarityProjectId);
      Clarity.identify(userStore.userDetails.id);
      window.clarity('set', 'app', import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME);

      router.afterEach((to) => {
        window.clarity('set', 'page', to.fullPath);
      });
    }
  }
};
